import { useState, useMemo } from 'react'
import { X, Check, Info, AlertTriangle } from 'lucide-react'
import { Badge } from '../ui'

/**
 * VerificaForm — Form per aggiungere una verifica a un obiettivo
 *
 * Props:
 *   obiettivo    - Oggetto obiettivo
 *   pepId        - ID del PEP
 *   onConfirm    - callback(verificaData) — chiama API
 *   onClose      - callback per chiudere
 */

const ESITO_OPTIONS = [
  { value: 'raggiunto', label: 'Raggiunto', variant: 'success' },
  { value: 'parziale', label: 'Parziale', variant: 'warning' },
  { value: 'non_raggiunto', label: 'Non raggiunto', variant: 'error' },
  { value: 'ricalibrato', label: 'Ricalibrato', variant: 'default' },
  { value: 'sospeso', label: 'Sospeso', variant: 'default' }
]

const ESITO_CALLOUT = {
  raggiunto: {
    icon: 'success',
    text: "L'obiettivo verrà marcato come raggiunto e chiuso. Non saranno più richieste verifiche."
  },
  parziale: {
    icon: 'info',
    text: "L'obiettivo resta attivo. La prossima verifica verrà programmata automaticamente in base alla frequenza."
  },
  non_raggiunto: {
    icon: 'info',
    text: "L'obiettivo resta attivo. Considera di rivedere la strategia o ricalibrarare il target."
  },
  ricalibrato: {
    icon: 'warning',
    text: "L'obiettivo resta attivo con parametri ricalibrati. Aggiorna baseline o target dalla scheda obiettivo dopo la verifica."
  },
  sospeso: {
    icon: 'warning',
    text: "L'obiettivo verrà sospeso. Non saranno programmate verifiche fino alla riattivazione."
  }
}

export default function VerificaForm({ obiettivo: ob, pepId, onConfirm, onClose }) {
  const [dataEvento, setDataEvento] = useState(toDateInputValue(new Date()))
  const [esito, setEsito] = useState('')
  const [valoreQuantitativo, setValoreQuantitativo] = useState(() => {
    // Pre-compila con l'ultimo valore se automatico
    if (ob.indicatore?.tipo === 'automatico') {
      const verifiche = ob.verifiche || []
      if (verifiche.length > 0) {
        const ultimo = verifiche[verifiche.length - 1]
        return ultimo.valoreQuantitativo != null ? String(ultimo.valoreQuantitativo) : ''
      }
      return ob.indicatore.valoreBaseline != null ? String(ob.indicatore.valoreBaseline) : ''
    }
    return ''
  })
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState({})

  const isAutomatico = ob.indicatore?.tipo === 'automatico'

  // Calcola prossima verifica
  const prossimaVerifica = useMemo(() => {
    if (!dataEvento || !ob.frequenzaVerifica) return null
    const mesiMap = {
      mensile: 1,
      bimestrale: 2,
      trimestrale: 3,
      semestrale: 6
    }
    const mesi = mesiMap[ob.frequenzaVerifica]
    if (!mesi) return null
    const d = new Date(dataEvento)
    d.setMonth(d.getMonth() + mesi)
    return d
  }, [dataEvento, ob.frequenzaVerifica])

  // Stato attuale dall'ultima verifica
  const statoAttuale = useMemo(() => {
    if (!isAutomatico) return null
    const verifiche = ob.verifiche || []
    const base = ob.indicatore.valoreBaseline
    const target = ob.indicatore.valoreTarget
    if (base == null || target == null) return null

    const ultimo = verifiche.length > 0 ? verifiche[verifiche.length - 1] : null
    const current = ultimo?.valoreQuantitativo ?? base
    const progresso = target !== base
      ? Math.max(0, Math.min(100, Math.round(((current - base) / (target - base)) * 100)))
      : 0

    return { current, base, target, progresso, etichetta: ob.indicatore.etichettaValore || '' }
  }, [ob, isAutomatico])

  const callout = esito ? ESITO_CALLOUT[esito] : null

  const validate = () => {
    const errs = {}
    if (!dataEvento) errs.dataEvento = 'La data è obbligatoria'
    if (!esito) errs.esito = "L'esito è obbligatorio"
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return

    setSubmitting(true)
    try {
      const data = {
        dataEvento: new Date(dataEvento).toISOString(),
        esito,
        note: note.trim() || undefined,
        valoreQuantitativo: valoreQuantitativo !== '' ? Number(valoreQuantitativo) : undefined,
        etichettaValore: isAutomatico ? ob.indicatore.etichettaValore : undefined
      }

      await onConfirm(data)
    } catch {
      // errore gestito dal parent
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              Nuova verifica
            </h3>
            <p className="text-sm text-gray-500 truncate mt-0.5">{ob.titolo}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Stato attuale (solo automatico) */}
          {statoAttuale && (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
              <p className="text-xs font-medium text-gray-500 mb-2">Stato attuale indicatore</p>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">
                  {statoAttuale.base} {statoAttuale.etichetta}
                </span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {statoAttuale.current} {statoAttuale.etichetta}
                </span>
                <span className="text-gray-600 dark:text-gray-400">
                  {statoAttuale.target} {statoAttuale.etichetta}
                </span>
              </div>
              <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden mt-2">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    statoAttuale.progresso >= 80 ? 'bg-green-500'
                    : statoAttuale.progresso >= 40 ? 'bg-yellow-500'
                    : 'bg-red-400'
                  }`}
                  style={{ width: `${statoAttuale.progresso}%` }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-1 text-right">{statoAttuale.progresso}%</p>
            </div>
          )}

          {/* Data */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Data verifica *
            </label>
            <input
              type="date"
              value={dataEvento}
              onChange={(e) => setDataEvento(e.target.value)}
              max={toDateInputValue(new Date())}
              className={`form-input w-full ${errors.dataEvento ? 'border-red-500' : ''}`}
            />
            {errors.dataEvento && <p className="text-xs text-red-500 mt-1">{errors.dataEvento}</p>}
          </div>

          {/* Esito */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Esito *
            </label>
            <select
              value={esito}
              onChange={(e) => setEsito(e.target.value)}
              className={`form-input w-full ${errors.esito ? 'border-red-500' : ''}`}
            >
              <option value="">Seleziona esito...</option>
              {ESITO_OPTIONS.map(e => (
                <option key={e.value} value={e.value}>{e.label}</option>
              ))}
            </select>
            {errors.esito && <p className="text-xs text-red-500 mt-1">{errors.esito}</p>}
          </div>

          {/* Callout esito */}
          {callout && (
            <div className={`flex items-start gap-2.5 rounded-lg p-3 text-xs ${
              callout.icon === 'success'
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                : callout.icon === 'warning'
                  ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
                  : 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
            }`}>
              {callout.icon === 'success' ? (
                <Check className="h-3.5 w-3.5 text-green-500 mt-0.5 flex-shrink-0" />
              ) : callout.icon === 'warning' ? (
                <AlertTriangle className="h-3.5 w-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
              ) : (
                <Info className="h-3.5 w-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
              )}
              <span className={
                callout.icon === 'success' ? 'text-green-700 dark:text-green-400'
                : callout.icon === 'warning' ? 'text-amber-700 dark:text-amber-400'
                : 'text-blue-700 dark:text-blue-400'
              }>
                {callout.text}
              </span>
            </div>
          )}

          {/* Valore quantitativo (indicatore automatico o opzionale) */}
          {isAutomatico && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Valore attuale {ob.indicatore.etichettaValore && `(${ob.indicatore.etichettaValore})`}
              </label>
              <input
                type="number"
                value={valoreQuantitativo}
                onChange={(e) => setValoreQuantitativo(e.target.value)}
                placeholder="Valore misurato"
                className="form-input w-full"
              />
              <p className="text-xs text-gray-400 mt-1">
                Pre-compilato dall'ultimo valore. Modifica se necessario.
              </p>
            </div>
          )}

          {/* Prossima verifica (auto-calcolata) */}
          {prossimaVerifica && esito && !['raggiunto', 'sospeso'].includes(esito) && (
            <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2">
              <Info className="h-3.5 w-3.5 text-gray-400" />
              <span>
                Prossima verifica programmata:{' '}
                <strong>
                  {prossimaVerifica.toLocaleDateString('it-IT', {
                    day: 'numeric', month: 'long', year: 'numeric'
                  })}
                </strong>
                {' '}({ob.frequenzaVerifica})
              </span>
            </div>
          )}

          {/* Note */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Note
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Osservazioni sulla verifica..."
              rows={3}
              className="form-input w-full resize-none"
              maxLength={2000}
            />
            <p className="text-xs text-gray-400 text-right mt-1">{note.length}/2000</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            Annulla
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="btn-primary flex items-center"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Salvataggio...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-1" />
                Salva verifica
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Helpers ─────────────────────────────────────────────────

function toDateInputValue(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}
